import {Empty, Spin,} from 'antd';
import React, { ReactNode, useEffect, useState } from 'react'
import moment, { Moment } from 'moment';
import { DT0Format } from '../../../../../utils/StaticVariable';
import { ChartBox } from '../../../ResultPanel/component/ChartBox';
import { IGetBasisChartV2ApiPayload } from '../../../../../types/chart';
import { IEnrichedMetadata, IGetMetadata } from '../../../../../types/metadata';
import {ADVANCED_ANALYTICS, IBestDataset, IItemsValidation} from '../../../../../types/analytics';
import { MarkerItem } from '../../../../../types/home';
import {  DownloadCheckboxKey, SCATTER_PLOT_VALIDATION } from '../../../../../utils/common';
import Api from '../../../../../api/Api';
import { LoadingOutlined } from '@ant-design/icons';
import { UserGroupPermissions } from '../../../../../types/userGroupPermissions';
import { isEmpty } from 'underscore';
import { ICreateFullextractionResult } from '../../../../../types/service';
import useDatasetStore from "../../../../../store/datasetStore";
import {usePointStore} from "../../../../../store/pointStore";
import { Button, DatePicker, Select, Typography } from 'antd';
import { AnalyticsSection } from "../../../Analytics/components/Layout/AnalyticsSection";

const { Text } = Typography;

interface IScatterPlot {
  validInSituPoint?: boolean
  dateRange?: [moment.Moment, moment.Moment]
  activePoint?: MarkerItem
  enrichedMetadata?: IEnrichedMetadata[]
  insituName?: string
  listParameters: IItemsValidation[],
  activeTab: number
  activeCategoryKey: string
  setActiveCategory: Function
  userGroupPermissions: UserGroupPermissions;
  datasetId: string
  bestDataset?: IBestDataset[]
  activeInsituType: string
  cellSize: string
  waterDepth: string
  notValidInsitu: ReactNode
  datasetChange: (item: string) => void
  variable: string | number
  setVariable: (item: string | number) => void
  startDate: Moment | undefined
  setStartDate: (item: Moment) => void
  endDate: Moment | undefined
  setEndDate: (item: Moment) => void
}

const ScatterPlot: React.FC<IScatterPlot> = ({
  activeCategoryKey,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  insituName,
  listParameters,
  dateRange,
  activeTab,
  userGroupPermissions,
  datasetId,
  bestDataset,
  activeInsituType,
  cellSize,
  waterDepth,
  notValidInsitu,
  variable,
  setVariable,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  datasetChange,
}) => {
  const [scatterPlotImages, setScatterPlotImages] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [tempEnrichedData, setTempEnrichedData] = useState<{Result : ICreateFullextractionResult}>()
  const [tempMetadata, setTempMetadata] = useState({})
  const fwePoints = usePointStore(state => state.fwePoints)
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs)

  // TODO: enable cache
  // const upsertScatterPlotCache = useValidationCacheStore(state => state.upsertScatterPlotCache)

  // useEffect(() => {
  //   if (!datasetId || !activePoint?.coordinates) return;

  //   const key = `${datasetId}_${variable}_${activePoint?.coordinates.join(',')}`;
  //   upsertScatterPlotCache(key, { variable, startDate, endDate, scatterPlotImages });
  // }, [datasetId, activePoint?.coordinates, variable, startDate, endDate, scatterPlotImages]);

  // useEffect(() => {
  //   setScatterPlotImages('')
  //   setError('')
  // }, [activePoint?.coordinates, datasetId])

  useEffect(() => {
    if (enrichedMetadata && listParameters.length > 0 && userGroupPermissions && validInSituPoint && dateRange ) {
      let parameter = listParameters[0].name
      if(!isEmpty(variable)){
        parameter = variable.toString()
      }
      setVariable(parameter)
      setStartDate(moment(dateRange[0]))
      setEndDate(moment(dateRange[1]))
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: datasetId,
        parameters: parameter,
        startDate: startDate?.format(DT0Format) ?? moment(dateRange[0]).format(DT0Format),
        endDate: endDate?.format(DT0Format) ?? moment(dateRange[1]).format(DT0Format),
      };
      if(activeTab === SCATTER_PLOT_VALIDATION && scatterPlotImages === '' && !error && !loading){
        onLoadScatterPlot(values, enrichedMetadata, activeCategoryKey)
          .catch(console.error);
      }
    }

  }, [
    activeTab,
    enrichedMetadata,
    datasetId,
    userGroupPermissions,
    validInSituPoint,
    dateRange,
    scatterPlotImages,
    loading,
    error
  ])

  const onLoadScatterPlot = async (
    values: IGetBasisChartV2ApiPayload,
    enrichedMetadata: IEnrichedMetadata[],
    type: string
  ) => {
    setLoading(true);
    setError('');
    setScatterPlotImages('');

    if (activeCategoryKey !== 'SW') {
      setLoading(false);
      return;
    }

    try {
      const category = bestDataset?.find(
        (bd: IBestDataset) => bd.id === values.datasetName
      );

      if (!category) {
        setLoading(false);
        return;
      }

      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === values.datasetName &&
          task.CoordinatesString === activePoint?.coordinates.join()
      );

      if (!task) {
        setLoading(false);
        return;
      }

      if (activeInsituType === 'mood') {
        let metadata : IGetMetadata = tempMetadata as IGetMetadata
        if(isEmpty(metadata)){
          metadata = await Api.metadata.getMetadata(`insitu-${insituName}`) as IGetMetadata
          setTempMetadata(metadata ?? {})
        }
        const findColIndex = metadata.ItemsInfo.find((item) => item.ShortName === values.parameters )?.Index

        let extractionInsituUpload = tempEnrichedData
        if(isEmpty(extractionInsituUpload)){
          extractionInsituUpload = await Api.service.createSubsetExtractionTask(
            `insitu-${insituName}`,
            values.startDate,
            values.endDate,
            activePoint?.coordinates!,
            insituName ?? '',
            cellSize,
            waterDepth,
            datasetBriefs,
            fwePoints
          );

          if (!extractionInsituUpload) {
            setError('Error extraction file');
            setLoading(false);
            return;
          }

          setTempEnrichedData(extractionInsituUpload)
        }

        const title = metadata.ItemsInfo.find((item) => item.ShortName === values.parameters)?.Name

        const res = await Api.chart.getJointScatterPlotImage({
          OrderId: task.OrderId,
          OrderId2: extractionInsituUpload?.Result.OrderIdList[0],
          OriginalOrderId: task.OrderId,
          Longitude: activePoint?.coordinates[0] ?? 0,
          Latitude: activePoint?.coordinates[1] ?? 0,
          StartDate: values.startDate,
          EndDate: values.endDate,
          Title: title ?? '',
          TitleY: title ?? '',
          AcronymX: values.parameters,
          AcronymY: values.parameters,
          ColumnIndexX: findColIndex ?? 1,
          ColumnIndexY: findColIndex ?? 1,
          IntervalCountX: 16,
          IntervalCountY: 16,
          AnalyticType: ADVANCED_ANALYTICS.SCATTER_PLOT_INSITU,
          Duration: '1',
          DatasetId: values.datasetName,
          DatasetId2: `insitu-${insituName}`,
          Sign1: 'below',
          Sign2: 'below',
          Month: 'All',
          PlotFlag: '2',
          PointName: activePoint?.label ?? ''
        });
        if (!res.img) {
          setError(res?.Error?.ErrorMessage ?? 'Error getting scatter plot images');
        } else {
          setScatterPlotImages('data:image/png;base64,' + res.img);
        }
      } else {
        const res = await Api.chart.getScatterPlotInsitu(
          insituName ?? '',
          task.OrderId,
          values.parameters,
          values.startDate,
          values.endDate,
          type,
          values.datasetName
        );

        if (!res) {
          setError(res?.Error?.ErrorMessage ?? 'Error getting scatter plot images');
        } else {
          setScatterPlotImages('data:image/png;base64,' + res.img);
        }
      }
    } catch (error) {
      setError('Error getting scatter plot images');
    } finally {
      setLoading(false);
    }
  }

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onVariableChange = (value: string | number) => {
    setVariable(value)
  }

  const handleUpdate = () => {
    if (enrichedMetadata && datasetId && startDate && endDate) {
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: datasetId,
        parameters: variable as string,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadScatterPlot(values, enrichedMetadata, activeCategoryKey)
        .catch(console.error);
    }
  }

  function disabledStartDate(current: Moment): boolean {
    const minDate = startDate;
    const maxDate = endDate;

    if(!validInSituPoint){
      return true
    }

    if (!current || !maxDate || !minDate) {
      return false;
    }

    const isBeforeMinDate = current.isBefore(minDate, 'day');
    const isAfterMaxDate = current.isAfter(maxDate, 'day');

    return isBeforeMinDate || isAfterMaxDate;
  }

  function disabledEndDate(current: Moment): boolean {
    const maxDate = endDate;

    if(!validInSituPoint){
      return true
    }

    if (!startDate) {
      return true;
    }

    const isBeforeStartDate = current.isBefore(startDate, 'day');
    const isAfterMaxDate = current.isAfter(maxDate, 'day');

    return isBeforeStartDate || isAfterMaxDate;
  }

  return (
    <AnalyticsSection
      plotInputContent={
        <div style={{display: 'flex', gap: '8px'}}>
          <div>
            <Text>Dataset</Text>
            <Select
              value={datasetId}
              style={{width: '100%', marginTop: '4px', minWidth: 220}}
              onChange={datasetChange}
              placeholder="Select dataset"
              disabled={!validInSituPoint || loading || activeCategoryKey !== DownloadCheckboxKey.SW}
            >
              {bestDataset?.map((item: IBestDataset) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.value}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Text>Variable</Text>
            <Select
              style={{width: '100%', marginTop: '4px', minWidth: 220}}
              placeholder="Select variable"
              onChange={onVariableChange}
              options={listParameters.map(item => ({
                value: item.name,
                label: `${item.details}${item.unit ? ` (${item.unit})` : ''}`
              }))}
              value={variable}
              disabled={!validInSituPoint || loading || activeCategoryKey !== DownloadCheckboxKey.SW}
            />
          </div>

          <div>
            <Text>Start Date</Text>
            <DatePicker
              onChange={onChangeStartDate}
              style={{width: '100%', marginTop: '4px'}}
              placeholder="Select start date"
              disabledDate={disabledStartDate || loading}
              disabled={loading || activeCategoryKey !== DownloadCheckboxKey.SW || !validInSituPoint}
              value={startDate ? moment(startDate) : null}
            />
          </div>

          <div>
            <Text>End Date</Text>
            <DatePicker
              onChange={onChangeEndDate}
              disabledDate={disabledEndDate || loading}
              disabled={loading || activeCategoryKey !== DownloadCheckboxKey.SW || !validInSituPoint}
              style={{width: '100%', marginTop: '4px'}}
              placeholder="Select end date"
              value={endDate ? moment(endDate) : null}
            />
          </div>
        </div>}
      updateContent={
        <Button
          type="primary"
          disabled={!validInSituPoint || activeCategoryKey !== DownloadCheckboxKey.SW || loading}
          onClick={handleUpdate}
          style={{width: 100, bottom: 10}}
        >
          Update
        </Button>
      }
      isLoading={loading}
      chartContent={
        validInSituPoint ? (
          <ChartBox
            key={'Scatter Plot'}
            title={'Scatter Plot'}
            height={loading ? '200px' : 'auto'}
            plot={
              activeCategoryKey !== DownloadCheckboxKey.SW ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  In-situ validation currently only support for waves dataset
                </div>
              ) : loading ? (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin/>} />
                </div>
              ) : error ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{error}</div>
              ) : scatterPlotImages.trim() !== "" ? (
                <img
                  style={{
                    width: '80%',
                    objectFit: 'scale-down',
                    maxHeight: '65vh',
                  }}
                  src={scatterPlotImages}
                  alt="ScatterPlot"
                />
              ) : (
                <Empty />
              )
            }
            
            dataset=''
          />
        ) : notValidInsitu
      }
    />
  )
}


export default ScatterPlot
